import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/FormField';
import { CharacterCount } from '@/components/ui/character-count';
import { PACKET_VALIDATION } from '@promo/shared';

interface WithdrawPacketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (justification: string) => void;
  isSubmitting?: boolean;
}

const MINIMUM_JUSTIFICATION_LENGTH = 10;

export function WithdrawPacketDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
}: WithdrawPacketDialogProps) {
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setJustification('');
      setError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    // Validate justification
    if (!justification.trim()) {
      setError('Justification is required');
      return;
    }

    if (justification.trim().length < MINIMUM_JUSTIFICATION_LENGTH) {
      setError(`Justification must be at least ${MINIMUM_JUSTIFICATION_LENGTH} characters`);
      return;
    }

    if (justification.length > PACKET_VALIDATION.MAX_WITHDRAWAL_JUSTIFICATION_LENGTH) {
      setError(
        `Justification must be ${PACKET_VALIDATION.MAX_WITHDRAWAL_JUSTIFICATION_LENGTH} characters or less`
      );
      return;
    }

    setError(null);
    onConfirm(justification.trim());
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[500px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Withdraw Promotion Packet</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to withdraw this promotion packet? This action cannot be undone.
            Please provide a justification for the withdrawal.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <FormField label="Justification for withdrawal" htmlFor="withdrawal-justification">
            <Textarea
              id="withdrawal-justification"
              placeholder="Please explain why this packet is being withdrawn..."
              value={justification}
              onChange={(e) => {
                setJustification(e.target.value);
                if (error) setError(null); // Clear error on input
              }}
              disabled={isSubmitting}
              className="min-h-[100px]"
              maxLength={PACKET_VALIDATION.MAX_WITHDRAWAL_JUSTIFICATION_LENGTH}
              required
            />
          </FormField>

          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/20 p-2 rounded">
              {error}
            </div>
          )}

          <CharacterCount
            current={justification.length}
            minimum={MINIMUM_JUSTIFICATION_LENGTH}
            max={PACKET_VALIDATION.MAX_WITHDRAWAL_JUSTIFICATION_LENGTH}
          />
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isSubmitting ? 'Withdrawing...' : 'Withdraw'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
